import { Environment } from "../utils/Environment";
import React, { useState, useEffect } from "react";
import { postWithUserTableParams } from "../services/fetchWrapper";
import Button from "@mui/material/Button";
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import HighlightOffIcon from "@mui/icons-material/HighlightOff";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import SuccessMessageComponent from "./SuccessMessageComponent";

const apiUrl = Environment.getApiUrl();

interface ProcessDiameterRequest {
    job_id: string;
    table_name: string;
    score_column: string;
    pipe_diameter_column: string;
    client_id: string;
}

const ProcessDiamScore: React.FC = () => {
    const [diamScoreJson, setDiamScoreJson] = useState<any | null>(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [showSuccess, setShowSuccess] = useState(false);
    const [pipeDiameterColumn, setPipeDiameterColumn] = useState<string>("");
    const [possibleColumns, setPossibleColumns] = useState<string[]>([]);
    const [processingDone, setProcessingDone] = useState(false);
    const [uploadStatus, setUploadStatus] = useState<string | null>(null);
    const [statusMessage, setStatusMessage] = useState<string | null>(null);

    // Fetch client data for scoring
    useEffect(() => {
        const fetchClientData = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";

            setLoading(true);
            setError(null);
            try {
                const response = await postWithUserTableParams(`${apiUrl}/get-client-data-for-diameter-scoring`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch client data");
                }
                const data = await response.json();
                if (data.status !== 'success' || typeof data.json !== 'object' || data.json === null) {
                    throw new Error("Invalid response format from backend");
                }
                setDiamScoreJson(data.json);
            } catch (err: any) {
                setError(err.message || "Failed to fetch client data");
            } finally {
                setLoading(false);
            }
        };
        fetchClientData();
    }, [apiUrl]);

    // Fetch possible diameter columns
    useEffect(() => {
        const fetchDiameterColumns = async () => {
            // Get values from localStorage at function run time
            const username = localStorage.getItem("userName") || "";
            const client_id = localStorage.getItem("client_id") || "";
            const job_id = localStorage.getItem("job_id") || "";

            setLoading(true);
            setError(null);
            try {
                // You may need to pass job_id, client_id, or table_name as needed by your backend
                const response = await postWithUserTableParams(`${apiUrl}/get-diameter-column`, {
                    username,
                    client_id,
                    job_id,
                });
                if (!response.ok) {
                    throw new Error("Failed to fetch diameter columns");
                }
                const data = await response.json();
                if (!Array.isArray(data.possible_material_columns)) {
                    throw new Error("Invalid response format for diameter columns");
                }
                const columnNames = data.possible_material_columns.map((col: any) => col.column_name);
                setPossibleColumns(columnNames);
                if (columnNames.length > 0) {
                    setPipeDiameterColumn(columnNames[0]);
                }
            } catch (err: any) {
                setError(err.message || "Failed to fetch diameter columns");
            } finally {
                setLoading(false);
            }
        };
        fetchDiameterColumns();
    }, [apiUrl]);

    const handleProcessDiameterScore = async () => {
        setLoading(true);
        setError(null);
        setUploadStatus("processing");
        setStatusMessage("Processing diameter scores...");
        try {
            if (!diamScoreJson) {
                throw new Error("No diameter score JSON to process");
            }
            if (!pipeDiameterColumn) {
                throw new Error("Please select a pipe diameter column.");
            }
            const request: ProcessDiameterRequest = {
                job_id: diamScoreJson.job_id,
                table_name: diamScoreJson.table_name,
                score_column: "cof_diam",
                pipe_diameter_column: pipeDiameterColumn, // <-- user-selected column
                client_id: diamScoreJson.client_id
            };
            const response = await fetch(`${apiUrl}/cof-diameter`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify(request),
            });
            if (!response.ok) {
                throw new Error("Check column name");
            }
            setShowSuccess(true);
            setUploadStatus("complete");
            setStatusMessage("Diameter scoring complete.");
            localStorage.setItem("diamScoreDone", "true");
        } catch (err: any) {
            setShowSuccess(false);
            setUploadStatus("failed");
            setStatusMessage(err.message || "Diameter scoring failed.");
        } finally {
            setProcessingDone(true);
            setLoading(false);
        }
    };

    return (
        <Box sx={{ maxWidth: 500, mx: "auto", mt: 4 }}>
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, textTransform: "uppercase" }}>Diameter Scoring</Typography>
            <svg width="292" height="6" viewBox="0 0 292 6" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginBottom: 16 }}>
                <path d="M0 3H292" stroke="#8AD019" strokeWidth="6" />
            </svg>
            <Typography variant="h5" sx={{ mb: 4, fontSize: 16 }}>
                Please confirm the column in your base data that contains pipe diameter measurements. If incorrect manually input the correct column.
            </Typography>
            {showSuccess ? (
                <SuccessMessageComponent />
            ) : (
                processingDone && (
                    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 2 }}>
                        <HighlightOffIcon sx={{ fontSize: 40, color: "#828282" }} />
                        <Typography variant="body2" sx={{ color: "#828282" }}>
                            Scoring did not complete successfully.
                        </Typography>
                    </Box>
                )
            )}
            <Typography variant="body2" sx={{ mb: 3, fontSize: 18, fontWeight: 700, maxWidth: 450, textAlign: "center" }}>
                Pipe Diameter Column
            </Typography>
            {possibleColumns.length > 0 && (
                <Autocomplete
                    freeSolo
                    options={possibleColumns}
                    value={pipeDiameterColumn}
                    inputValue={pipeDiameterColumn}
                    onChange={(_, value) => setPipeDiameterColumn(value || "")}
                    onInputChange={(_, value) => setPipeDiameterColumn(value || "")}
                    renderInput={(params) => (
                        <TextField {...params}
                            variant="outlined"
                            sx={{
                                mb: 2,
                                width: 300,
                                maxWidth: "100%",
                                "& .MuiOutlinedInput-root": {
                                    backgroundColor: "#F6FFE8",
                                    height: 36,
                                },
                                "& .MuiOutlinedInput-input": {
                                    padding: "8px 12px",
                                },
                            }}
                        />
                    )}
                    sx={{ mb: 2 }}
                />
            )}
            <Button
                variant="contained"
                color="primary"
                disabled={loading || !diamScoreJson || !pipeDiameterColumn}
                onClick={handleProcessDiameterScore}
                sx={{ backgroundColor: "#0373B5" }}
            >
                {loading ? "Processing..." : "Process diameter scores"}
            </Button>
            {uploadStatus && (
                <Box
                    sx={{
                        mt: 2,
                        p: 2,
                        border: "1px solid #ccc",
                        borderRadius: 2,
                        maxWidth: 500,
                        mx: "auto",
                        display: "flex",
                        flexDirection: "column",
                        alignItems: "center",
                    }}
                >
                    <Typography variant="h6">Processing Status</Typography>
                    <Typography variant="body2">Status: {uploadStatus}</Typography>
                    <Typography variant="body2">Message: {statusMessage}</Typography>
                </Box>
            )}
            {error && <div style={{ color: "red", marginTop: 8 }}>{error}</div>}
        </Box>
    );
};

export default ProcessDiamScore;